/**
 * useAudioMixer — Web Audio API hook for real-time audio mixing in the
 * audio remix modal.
 *
 * Routes the video element's audio through a GainNode (for ducking),
 * pre-decodes TTS clips into AudioBuffers, and schedules playback via
 * AudioBufferSourceNode synced to video.currentTime.
 *
 * Each clip has its own GainNode for individual on/off toggling.
 * 15ms crossfades prevent pops at transitions.
 */

import { useCallback, useEffect, useRef } from 'react'

const FADE_S = 0.015 // 15ms crossfade

export interface MixerClip {
  id: string
  url: string
  startS: number // position on video timeline
  endS: number
  enabled: boolean
}

interface ActiveSource {
  source: AudioBufferSourceNode
  clipId: string
}

export function useAudioMixer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
) {
  const ctxRef = useRef<AudioContext | null>(null)
  const videoGainRef = useRef<GainNode | null>(null)
  const clipGainsRef = useRef<Map<string, GainNode>>(new Map())
  const buffersRef = useRef<Map<string, AudioBuffer>>(new Map())
  const activeSourcesRef = useRef<Map<string, ActiveSource>>(new Map())
  const clipsRef = useRef<MixerClip[]>([])
  const rafRef = useRef<number>(0)
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const initializedRef = useRef(false)

  // ── Initialize AudioContext and wire video element ──
  const init = useCallback(() => {
    const video = videoRef.current
    if (!video || initializedRef.current) return

    const ctx = new AudioContext()
    ctxRef.current = ctx

    // Route video audio through Web Audio API
    const mediaSource = ctx.createMediaElementSource(video)
    mediaSourceRef.current = mediaSource

    const videoGain = ctx.createGain()
    videoGainRef.current = videoGain

    mediaSource.connect(videoGain)
    videoGain.connect(ctx.destination)

    initializedRef.current = true
  }, [videoRef])

  // ── Decode a clip URL into an AudioBuffer ──
  const decodeClip = useCallback(async (id: string, url: string) => {
    const ctx = ctxRef.current
    if (!ctx || buffersRef.current.has(id)) return

    try {
      const resp = await fetch(url)
      if (!resp.ok) return
      const arrayBuf = await resp.arrayBuffer()
      const audioBuf = await ctx.decodeAudioData(arrayBuf)
      buffersRef.current.set(id, audioBuf)

      // Create a gain node for this clip
      if (!clipGainsRef.current.has(id)) {
        const gain = ctx.createGain()
        gain.gain.value = 0
        gain.connect(ctx.destination)
        clipGainsRef.current.set(id, gain)
      }
    } catch (e) {
      console.warn(`[AudioMixer] Failed to decode clip ${id}:`, e)
    }
  }, [])

  // ── Update the clips list (call on every highlight/position change) ──
  const updateClips = useCallback((clips: MixerClip[]) => {
    clipsRef.current = clips
  }, [])

  // ── The sync loop ──
  const syncLoop = useCallback(() => {
    const ctx = ctxRef.current
    const video = videoRef.current
    if (!ctx || !video) {
      rafRef.current = requestAnimationFrame(syncLoop)
      return
    }

    if (video.paused) {
      rafRef.current = requestAnimationFrame(syncLoop)
      return
    }

    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    const now = ctx.currentTime
    const t = video.currentTime
    const clips = clipsRef.current

    // Determine which clips should be active
    const activeClipIds = new Set<string>()
    let shouldDuck = false

    for (const clip of clips) {
      if (!clip.enabled) continue
      if (!buffersRef.current.has(clip.id)) continue
      if (t >= clip.startS && t < clip.endS) {
        activeClipIds.add(clip.id)
        shouldDuck = true
      }
    }

    // Duck video audio
    const videoGain = videoGainRef.current
    if (videoGain) {
      const current = videoGain.gain.value
      const target = shouldDuck ? 0.0 : 1.0
      if (Math.abs(current - target) > 0.01) {
        videoGain.gain.cancelScheduledValues(now)
        videoGain.gain.setValueAtTime(current, now)
        videoGain.gain.linearRampToValueAtTime(target, now + FADE_S)
      }
    }

    // Start/stop clip sources
    for (const clip of clips) {
      const buffer = buffersRef.current.get(clip.id)
      const clipGain = clipGainsRef.current.get(clip.id)
      if (!buffer || !clipGain) continue

      if (activeClipIds.has(clip.id)) {
        // Should be playing
        if (!activeSourcesRef.current.has(clip.id)) {
          // Start new source at correct offset
          const offset = t - clip.startS
          const remaining = clip.endS - t

          const source = ctx.createBufferSource()
          source.buffer = buffer
          source.connect(clipGain)

          // Fade in
          clipGain.gain.cancelScheduledValues(now)
          clipGain.gain.setValueAtTime(0, now)
          clipGain.gain.linearRampToValueAtTime(1.0, now + FADE_S)

          source.start(0, Math.max(0, offset), remaining + FADE_S)

          source.onended = () => {
            activeSourcesRef.current.delete(clip.id)
            if (clipGain) {
              clipGain.gain.cancelScheduledValues(ctx.currentTime)
              clipGain.gain.setValueAtTime(clipGain.gain.value, ctx.currentTime)
              clipGain.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE_S)
            }
          }

          activeSourcesRef.current.set(clip.id, { source, clipId: clip.id })
        }
      } else {
        // Should not be playing — stop if active
        const active = activeSourcesRef.current.get(clip.id)
        if (active) {
          // Fade out then stop
          clipGain.gain.cancelScheduledValues(now)
          clipGain.gain.setValueAtTime(clipGain.gain.value, now)
          clipGain.gain.linearRampToValueAtTime(0, now + FADE_S)
          try { active.source.stop(now + FADE_S) } catch { /* already stopped */ }
          activeSourcesRef.current.delete(clip.id)
        }
      }
    }

    rafRef.current = requestAnimationFrame(syncLoop)
  }, [videoRef])

  // ── Video event handlers ──
  const handlePlay = useCallback(() => {
    const ctx = ctxRef.current
    if (ctx?.state === 'suspended') ctx.resume()
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(syncLoop)
  }, [syncLoop])

  const handlePause = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    activeSourcesRef.current.forEach(({ source }) => {
      try { source.stop() } catch { /* already stopped */ }
    })
    activeSourcesRef.current.clear()
  }, [])

  const handleSeeked = useCallback(() => {
    // AudioBufferSourceNode is one-shot — must recreate after seek
    activeSourcesRef.current.forEach(({ source }) => {
      try { source.stop() } catch { /* already stopped */ }
    })
    activeSourcesRef.current.clear()
  }, [])

  // ── Wire video events ──
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('seeked', handleSeeked)

    // Start RAF for playhead tracking even when paused
    rafRef.current = requestAnimationFrame(syncLoop)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('seeked', handleSeeked)
      cancelAnimationFrame(rafRef.current)
    }
  }, [videoRef, handlePlay, handlePause, handleSeeked, syncLoop])

  // ── Cleanup ──
  useEffect(() => {
    const sources = activeSourcesRef.current
    const ctx = ctxRef.current
    const raf = rafRef.current
    return () => {
      cancelAnimationFrame(raf)
      sources.forEach(({ source }) => {
        try { source.stop() } catch { /* already stopped */ }
      })
      ctx?.close()
    }
  }, [])

  return {
    init,
    decodeClip,
    updateClips,
    /** Current playhead time — read from videoRef in syncLoop */
    getPlayheadTime: () => videoRef.current?.currentTime ?? 0,
  }
}
